import {
  memo,
  useCallback,
  useRef,
  useState,
  type ImgHTMLAttributes,
  type Ref,
} from "react"
import { IMAGE_SIZES } from "@/constants/ui"
import { PhotoUrl, PlacePhoto } from "@/types/place"
import { cn } from "cn"

import cloudflareLoader from "@/lib/cloudflare-loader"
import { isNumber } from "@/lib/utils/assertions"
import { usePhotoDeferral } from "./photo-deferral"
import { usePhotoSources, type PhotoSource } from "./photo-sources"
import { Skeleton } from "./skeleton"

export interface PhotoProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "onLoad" | "onError"
> {
  src: PhotoUrl | PlacePhoto | string
  width: keyof typeof IMAGE_SIZES
  height?: number
  fit?: "cover" | "contain" | "scale-down"
  blur?: number
  useLoadingSkeleton?: boolean
  onLoad?: () => void
  onError?: () => void
  priority?: boolean
  /**
   * Larger renditions from a breakpoint up, rendered as a <picture> so the
   * browser picks one before any script runs. `width` is the rendition below
   * the smallest breakpoint.
   */
  sources?: PhotoSource[]
}

// The rendition the browser loaded, which with `sources` isn't `width`'s.
const renditionOf = (img: HTMLImageElement) => img.currentSrc || img.src

const Photo = memo(function Photo({
  ref,
  src,
  width,
  height,
  alt,
  fit,
  className,
  onLoad,
  onError,
  blur,
  useLoadingSkeleton,
  loading = "lazy",
  priority,
  sources,
  ...props
}: PhotoProps & { ref?: Ref<HTMLImageElement> }) {
  const [loaded, setLoaded] = useState(false)
  const shouldShowSkeleton = !!useLoadingSkeleton && !loaded
  // The rendition whose load or error has been handled. A photo in server HTML
  // can finish before hydration, when React isn't listening yet, so setRefs
  // handles it; for a cached photo the browser may still fire the event after.
  const settledSrcRef = useRef("")

  const srcString = typeof src === "string" ? src : src.url
  // Inside a carousel the rendition is held back until the photo is nearly on screen,
  // with a blurred stand-in underneath meanwhile; anywhere else this is a no-op.
  const { shouldLoad, previewUrl, observerRef } = usePhotoDeferral({
    src: srcString,
    priority,
  })
  const gated = !!previewUrl

  const handleLoad = useCallback(
    async (img: HTMLImageElement) => {
      if (settledSrcRef.current === renditionOf(img)) return
      settledSrcRef.current = renditionOf(img)
      setLoaded(true)
      onLoad?.()

      if (onError && renditionOf(img)) {
        try {
          const response = await fetch(renditionOf(img), {
            method: "HEAD",
          })
          if (response.status === 404) {
            onError()
          }
        } catch (error) {
          // ignore fetch errors, caught by onError handler
        }
      }
    },
    [onLoad, onError]
  )

  const handleError = useCallback(
    (img: HTMLImageElement) => {
      if (settledSrcRef.current === renditionOf(img)) return
      settledSrcRef.current = renditionOf(img)
      setLoaded(false)
      onError?.()
    },
    [onError]
  )

  const setRefs = useCallback(
    (node: HTMLImageElement | null) => {
      observerRef(node)
      if (typeof ref === "function") {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
      if (node?.complete && renditionOf(node)) {
        if (node.naturalWidth > 0) void handleLoad(node)
        else handleError(node)
      }
    },
    [observerRef, ref, handleLoad, handleError]
  )

  const urlAt = (size: keyof typeof IMAGE_SIZES) =>
    cloudflareLoader({
      src: srcString,
      width: IMAGE_SIZES[size],
      height: height
        ? isNumber(height)
          ? height
          : parseInt(String(height))
        : undefined,
      fit,
      blur,
    }) || srcString

  const { pictureSources, srcWidth } = usePhotoSources(width, sources)
  const fullUrl = urlAt(srcWidth)

  // The stand-in has to sit in exactly the box the real photo will, so it takes the
  // caller's geometry as well — a card backdrop offset to 200% width has a stand-in
  // offset the same way — and only falls back to filling its container when the caller
  // left geometry to CSS elsewhere.
  const layerClassName = cn(
    "overflow-hidden rounded-[inherit] object-cover",
    width &&
      height && [
        fit === "contain" && "object-contain",
        fit === "scale-down" && "object-scale-down",
      ],
    className
  )

  const img = (
    <img
      ref={setRefs}
      src={shouldLoad ? fullUrl : undefined}
      width={IMAGE_SIZES[width]}
      height={height}
      onLoad={(e) => void handleLoad(e.currentTarget)}
      onError={(e) => handleError(e.currentTarget)}
      alt={alt || ""}
      className={cn(
        layerClassName,
        // Fade up from the stand-in rather than appearing mid-decode.
        gated && "transition-opacity duration-500 ease-out",
        gated && !loaded && "opacity-0"
      )}
      data-slot="photo"
      loading={priority ? "eager" : loading || "lazy"}
      fetchPriority={priority ? "high" : undefined}
      // disable pinterest hover
      {...{ nopin: "nopin" }}
      {...props}
    />
  )

  return (
    <>
      {shouldShowSkeleton && (
        <Skeleton className="absolute inset-0 rounded-[inherit]" />
      )}
      {/* Outlives the fade: dropping it as the real photo starts fading in
          would flash the bare card. */}
      {previewUrl && (
        <img
          src={previewUrl}
          alt=""
          aria-hidden
          // Decorative and on top until the fade ends, so it must never take a click or
          // swallow a drag the card set up.
          className={cn(
            layerClassName,
            !className && "absolute inset-0 size-full",
            "pointer-events-none"
          )}
          data-slot="photo-preview"
        />
      )}
      {pictureSources?.length && shouldLoad ? (
        // The img inherits its radius through the picture.
        <picture className="rounded-[inherit]">
          {pictureSources.map((source) => (
            <source
              key={source.media}
              media={source.media}
              srcSet={urlAt(source.width)}
            />
          ))}
          {img}
        </picture>
      ) : (
        img
      )}
    </>
  )
})

Photo.displayName = "Photo"

export { Photo }
